// Yakıt Modülü — Server-Sent Events Stream Service (Singleton)
//
// Backend `/fuel/request/stream` endpoint'i üzerinden anlık event push alır.
// Polling yerine kullanılır. Birden fazla component aynı stream'e subscribe olabilir.
//
// Kullanım:
//   import fuelStream from "@/services/fuelEventStream";
//   fuelStream.connect(token);
//   const off = fuelStream.on("new-request", (e) => { ... });
//   off(); // unsubscribe

import { fuel } from "@/networking/urlmanager";

const EVENT_TYPES = [
  "connected",
  "new-request",
  "request-updated",
  "request-completed",
  "request-cancelled",
  "item-added",
  "item-approved",
  "item-rejected",
];

class FuelEventStream {
  constructor() {
    this.es = null;
    this.token = null;
    this.connected = false;
    this.listeners = new Map(); // event type → Set of callbacks
    this.statusListeners = new Set(); // (status: 'connecting' | 'connected' | 'disconnected') => void
    this._reconnectAttempts = 0;
  }

  connect(token) {
    if (!token) {
      this._setStatus("disconnected");
      return;
    }
    // Aynı token ile zaten bağlıysa tekrar açma
    if (this.es && this.token === token && this.connected) return;
    // Önce mevcut bağlantıyı kapat
    this.close();

    this.token = token;
    this._setStatus("connecting");

    const url = fuel.requestStream + "?token=" + encodeURIComponent(token);
    console.log("[fuelStream] Bağlanılıyor:", fuel.requestStream);
    try {
      this.es = new EventSource(url);
    } catch (err) {
      console.warn("[fuelStream] EventSource oluşturulamadı", err);
      this._setStatus("disconnected");
      return;
    }

    // Named event'ler — backend named event olarak gönderiyorsa burada yakalanır
    EVENT_TYPES.forEach((type) => {
      this.es.addEventListener(type, (e) => {
        console.log("[fuelStream] (named) event geldi:", type, e.data);
        const payload = this._safeParse(e.data) || {};
        if (type === "connected") {
          this.connected = true;
          this._reconnectAttempts = 0;
          this._setStatus("connected");
        }
        this._dispatch(type, { type, ...payload });
      });
    });

    // Genel onmessage — { type: "...", ... } şeklinde gelir
    this.es.onmessage = (e) => {
      console.log("[fuelStream] (default) message geldi:", e.data);
      const payload = this._safeParse(e.data);
      if (!payload) return;
      const type = payload.type;
      if (type && EVENT_TYPES.includes(type)) {
        if (type === "connected") {
          this.connected = true;
          this._setStatus("connected");
        }
        this._dispatch(type, payload);
      }
    };

    this.es.onopen = () => {
      console.log("[fuelStream] Bağlantı açıldı (readyState=OPEN)");
      this.connected = true;
      this._reconnectAttempts = 0;
      this._setStatus("connected");
    };

    // Hata olduğunda — browser otomatik reconnect dener (3sn default)
    this.es.onerror = (err) => {
      console.warn("[fuelStream] Hata / bağlantı kapandı", err, "readyState=", this.es?.readyState);
      this.connected = false;
      this._setStatus("disconnected");
      this._reconnectAttempts += 1;
      // ReadyState 2 (CLOSED) ise manuel olarak yeniden açmaya zorla
      if (this.es && this.es.readyState === EventSource.CLOSED) {
        // Browser reconnect yapmıyor demektir, manuel reconnect 5sn sonra
        setTimeout(() => {
          if (this.token) this.connect(this.token);
        }, 5000);
      }
    };
  }

  close() {
    if (this.es) {
      try { this.es.close(); } catch (e) { /* noop */ }
      this.es = null;
    }
    this.connected = false;
    this._setStatus("disconnected");
  }

  // Token değiştiyse yeniden bağlan
  refresh(token) {
    this.close();
    this.connect(token);
  }

  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);
    // unsubscribe fonksiyonu döndür
    return () => this.off(eventType, callback);
  }

  off(eventType, callback) {
    const set = this.listeners.get(eventType);
    if (set) set.delete(callback);
  }

  onStatus(callback) {
    this.statusListeners.add(callback);
    // Çağıran component mevcut durumu hemen alabilsin
    try { callback(this.connected ? "connected" : "disconnected"); } catch (e) { /* noop */ }
    return () => this.statusListeners.delete(callback);
  }
  isConnected() { return this.connected; }

  _dispatch(eventType, payload) {
    const set = this.listeners.get(eventType);
    const count = set ? set.size : 0;
    console.log("[fuelStream] dispatch:", eventType, "abone sayısı:", count);
    if (!set) return;
    set.forEach((cb) => {
      try { cb(payload); } catch (err) { console.warn("[fuelStream] listener hatası", err); }
    });
  }

  _setStatus(status) {
    this.statusListeners.forEach((cb) => {
      try { cb(status); } catch (err) { /* noop */ }
    });
  }

  _safeParse(data) {
    try { return JSON.parse(data); } catch (e) { return null; }
  }
}

// Singleton export
const instance = new FuelEventStream();
export default instance;
